import { LinearGradient } from 'expo-linear-gradient';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import ArrowLeft from 'lucide-react-native/icons/arrow-left';
import Calendar from 'lucide-react-native/icons/calendar';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { ArticleHead } from '@/components/detail/article-head';
import { ArticleRelated } from '@/components/detail/article-related';
import { FiltersBar } from '@/components/riviera/filters-bar';
import { PortableTextBody } from '@/components/riviera/portable-text-body';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { Badge } from '@/components/ui/badge';
import { Fonts, Spacing } from '@/constants/theme';
import { FiltersProvider } from '@/hooks/use-filters';
import { useArticle } from '@todo/core/hooks/use-article';
import { useTheme } from '@/hooks/use-theme';
import { buildArticleLocaleLinks } from '@/lib/detail/article-detail';
import { articleImageAlt } from '@/lib/i18n/article-content';
import { formatDateMedium } from '@todo/core/i18n/format';
import { LocaleLinksProvider } from '@todo/core/i18n/locale-links-context';
import { useLocale } from '@todo/core/i18n/use-locale';
import { articlesIndexSeedKey } from '@todo/core/list-queries';
import { urlForImage } from '@todo/core/sanity/client';
import { type ArticleDetail } from '@todo/core/sanity/queries';
import { getPrefetched } from '@todo/core/static-prefetch';

import css from '@/components/detail/detail-desktop.module.css';

export { generateArticleStaticParams as generateStaticParams } from '@/lib/detail/article-detail';

type Translate = (key: string, options?: Record<string, unknown>) => string;
type Theme = ReturnType<typeof useTheme>;

function Hero({
  heroUri,
  alt,
  theme,
  onBack,
  t,
}: {
  heroUri: string | null;
  alt: string;
  theme: Theme;
  onBack: () => void;
  t: Translate;
}) {
  return (
    <>
      {heroUri ? (
        // Raw <img> (not expo-image) so expo's static export bakes the hero —
        // the page's LCP element — into the server-rendered HTML instead of
        // waiting for hydration to insert it. See hero-search.web.tsx.
        <img
          src={heroUri}
          alt={alt}
          className={css.heroImg}
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
      ) : (
        <View style={[styles.heroFill, { backgroundColor: theme.muted }]} />
      )}
      <LinearGradient colors={['rgba(0,0,0,0.45)', 'rgba(0,0,0,0)']} style={styles.heroScrim} />
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('a11y.back')}
        onPress={onBack}
        style={({ pressed }) => [styles.backBtn, pressed && { opacity: 0.7 }]}>
        <ArrowLeft size={20} color="#fff" />
      </Pressable>
      <View style={styles.heroBadge}>
        <Badge variant="gold">{t('articles.badge')}</Badge>
      </View>
    </>
  );
}

// Web-only: one responsive tree for the mobile and desktop layouts (see
// detail-desktop.module.css), resolved at first paint rather than the native
// screen's post-mount `isLg` swap. Head + static params are shared with the
// native file.
export default function ArticleScreen() {
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const { article: fetchedArticle, loading, error } = useArticle(slug, locale);

  const prefetchKey = slug ? `${locale}:${slug}` : undefined;
  const article = fetchedArticle ?? getPrefetched<ArticleDetail>('article', prefetchKey) ?? null;

  const heroUri = article?.mainImage
    ? urlForImage(article.mainImage).width(1600).height(900).url()
    : null;
  const localeLinks = buildArticleLocaleLinks(article, locale);

  const onBack = () => router.back();

  const dateRow =
    article?.publishedAt ? (
      <View style={styles.metaRow}>
        <Calendar size={14} color={theme.mutedForeground} />
        <Text style={[styles.date, { color: theme.mutedForeground }]}>
          {formatDateMedium(article.publishedAt, locale)}
        </Text>
      </View>
    ) : null;

  return (
    <LocaleLinksProvider value={localeLinks}>
    <FiltersProvider>
      <View style={[styles.root, { backgroundColor: theme.background }]}>
        <Stack.Screen options={{ headerShown: false }} />
        <ArticleHead
          article={article}
          locale={locale}
          slug={slug}
          notFoundTitle={t('articles.notFound')}
          relatedSeedKey={articlesIndexSeedKey(locale)}
        />
        <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
          <SiteHeader />
        </SafeAreaView>
        <ScrollView
          style={{ flex: 1 }}>
          {article ? (
            <>
              {/* One tree for both breakpoints — mobile: full-bleed hero +
                  overlapping card; desktop: filters sidebar + a text-forward
                  reading column with the hero under the title. The regrouping
                  is pure CSS (display: contents — see
                  detail-desktop.module.css), so the title, hero and article
                  body are in the HTML once. */}
              <div className={css.shell}>
                <aside className={css.shellSidebar}>
                  {/* Desktop-only sidebar: skip the chip-scroller markup. */}
                  <FiltersBar variant="desktop" />
                </aside>
                <div className={css.shellMain}>
                  <div className={css.articleDetail}>
                    <div className={css.detailMedia}>
                      <Hero
                        heroUri={heroUri}
                        alt={articleImageAlt(article)}
                        theme={theme}
                        onBack={onBack}
                        t={t}
                      />
                    </div>

                    <div
                      className={css.detailCard}
                      style={{ backgroundColor: theme.card, borderColor: theme.border }}>
                      <header className={css.detailHeader}>
                        <h1
                          className={css.detailTitle}
                          style={{ color: theme.text, fontFamily: Fonts?.serif }}>
                          {article.title}
                        </h1>
                        {dateRow}
                      </header>

                      {/* On desktop the hero separates the header from the
                          body instead. */}
                      <div className={css.mobileOnly}>
                        <View style={[styles.divider, { backgroundColor: theme.border }]} />
                      </div>

                      <div className={css.articleBody}>
                        <PortableTextBody value={article.body} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* "Plan your trip" + "More articles" — bracketed by
                  data-lastmod-skip sentinels: packages/web-build/src/generate-sitemap.js
                  strips this region before hashing the body, so a new or
                  edited sibling article never restamps this page's lastmod. */}
              <div data-lastmod-skip="start" hidden />
              <ArticleRelated article={article} locale={locale} />
              <div data-lastmod-skip="end" hidden />
            </>
          ) : loading ? (
            <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
              {t('articles.loadingOne')}
            </Text>
          ) : error ? (
            <Text style={[styles.statusText, { color: theme.destructive }]}>
              {t('articles.loadErrorOne', { error: error.message })}
            </Text>
          ) : (
            <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
              {t('articles.notFound')}
            </Text>
          )}
          <SiteFooter />
        </ScrollView>
      </View>
    </FiltersProvider>
    </LocaleLinksProvider>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  statusText: {
    fontSize: 14,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.four,
  },
  heroFill: {
    width: '100%',
    height: '100%',
  },
  heroScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '40%',
  },
  backBtn: {
    position: 'absolute',
    top: Spacing.three,
    left: Spacing.three,
    width: 38,
    height: 38,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroBadge: {
    position: 'absolute',
    top: Spacing.three,
    right: Spacing.three,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: Spacing.two,
  },
  date: {
    fontSize: 13,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: Spacing.four,
  },
});
